import React, { useState, useCallback, useRef, useEffect } from 'react';
import StageModale from './StageModale';
import StageFenetreEnvoi, { BandeauSerie } from './StageFenetreEnvoi';
import StageFenetrePromotion from './StageFenetrePromotion';
import { stageAPI } from '../services/api';
import { useModelesEnvoiStage } from './FenetreModeleCourriel';

/**
 * SUIVI DE STAGE — la modale d'envoi d'un courriel (lot 3.2)
 * ===========================================================
 * Spec : doc/modules/suivi_de_stage.md, écarts E8 (« préparé »), E24 (deux portes)
 *
 * ── ⚠️ POURQUOI LES ADRESSES SE COLLENT, ET NE PARTENT PAS DANS L'URL ──────
 * Remesuré le 2026-09-16 sur les données réelles, par le service lui-même : le
 * message SEUL fait **469** caractères, et **2 300** une fois les 59 adresses
 * ajoutées en Cci — soit **31 caractères par adresse**, pour un plafond
 * `LONGUEUR_MAILTO_MAX` de 2 000 (limite documentée de `ShellExecute`, ~2 048).
 *
 * ⚠️ **Ce n'est PAS une impossibilité de principe** : sous 2 000 caractères,
 * **49 adresses tiennent**. Une promotion de 40 rentrerait entièrement dans le
 * message. C'est celle de 59 qui déborde, de 300 caractères.
 *
 * Et le mode de panne est **silencieux** : le système tronque la commande ou
 * l'ignore. Le client mail s'ouvrirait avec une liste de destinataires coupée au
 * milieu, et l'assistante enverrait à 45 étudiants sur 59 sans le savoir.
 *
 * La modale copie donc les adresses dans le presse-papier **dès son ouverture**,
 * et le `mailto:` ne porte que l'objet et le corps. Quatre gestes en tout —
 * deux ici, coller et envoyer dans le client mail — une fois par campagne.
 *
 * ── ⚠️ L'ÉCRAN NE DIT JAMAIS « ENVOYÉ » (écart E8) ────────────────────────
 * En `mailto:`, l'application perd la main dès que la fenêtre s'ouvre : si
 * l'assistante la ferme sans envoyer, rien ne le lui apprend. On dit donc
 * « préparé ». La seule preuve qu'un message est arrivé viendra de
 * `jetons.ouvert_le`, quand l'étudiant cliquera son lien.
 */

/**
 * ⚠️ LA MODALE NE NOMME PLUS PERSONNE EN DUR.
 *
 * Défaut trouvé par Bertrand à la recette du 2026-09-18 : sur l'envoi de
 * l'étape ③, elle annonçait « **Relancer** ABDELHAMID YALLAS » — alors que le
 * message part **au maître de stage**, et qu'il s'agissait d'un **premier**
 * envoi. Deux mensonges en quatre mots. Ces phrases dataient du lot 3.2, où le
 * seul envoi individuel était bel et bien une relance d'étudiant.
 *
 * ⚠️ Le remède n'est pas de réécrire les trois phrases : c'est que l'écran
 * ACCORDE sa phrase au destinataire que le serveur lui annonce
 * (`destinataire_role`, lu sur le modèle de courriel qui a servi à fabriquer le
 * message). Les lots 3.4, 3.5 et 3.6 amènent trois destinataires de plus —
 * chacun aurait rejoué le même défaut.
 *
 * ⚠️ Le repli « le destinataire » n'est pas un cache-misère : c'est exactement
 * ce que l'application sait quand le modèle ne précise rien. Mieux vaut une
 * phrase vraie et vague qu'une phrase précise et fausse.
 */
const ROLES = {
    etudiant:           { le: "l'étudiant",             a: "à l'étudiant",             Le: "L'étudiant" },
    maitre:             { le: 'le maître de stage',     a: 'au maître de stage',       Le: 'Le maître de stage' },
    tuteur:             { le: 'le tuteur',              a: 'au tuteur',                Le: 'Le tuteur' },
    maitre_et_etudiant: { le: "le maître de stage et l'étudiant",
                          a:  "au maître de stage et à l'étudiant",
                          Le: "Le maître de stage et l'étudiant" },
    etudiant_et_maitre: { le: "l'étudiant et le maître de stage",
                          a:  "à l'étudiant et au maître de stage",
                          Le: "L'étudiant et le maître de stage" }
};
const ROLE_INCONNU = { le: 'le destinataire', a: 'au destinataire', Le: 'Le destinataire' };


/**
 * Copie dans le presse-papier, avec un repli pour les navigateurs anciens.
 * Exportée (lot 3.7a) : l'annuaire des entreprises copie ses adresses de la même façon.
 */
export async function copier(texte) {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(texte);
            return true;
        }
    } catch (_) { /* on tente le repli */ }

    // ⚠️ Repli indispensable : `navigator.clipboard` n'existe qu'en contexte
    // sécurisé. En développement sur `http://localhost` il fonctionne, mais sur
    // une adresse en `http://` d'un réseau interne, non — et l'assistante se
    // retrouverait devant un bouton sans effet.
    try {
        const zone = document.createElement('textarea');
        zone.value = texte;
        zone.setAttribute('readonly', '');
        zone.style.position = 'fixed';
        zone.style.opacity = '0';
        document.body.appendChild(zone);
        zone.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(zone);
        return ok;
    } catch (_) {
        return false;
    }
}

/**
 * ⚠️ L'AIGUILLAGE (lot 3.S-b2). Un BROUILLON (`mode: 'smtp'` — le département envoie par le
 * serveur) ouvre la fenêtre d'envoi « façon Outlook » ; un `mailto` ouvre la modale d'avant,
 * inchangée. Les écrans appelants ne connaissent qu'un seul composant.
 */
function StageModaleEnvoi({ envoi, onFermer, departementId, onTermine, serie = null, rePreparer = null }) {
    // Lot MC (2026-10-28) : « Modèles de courriel… » — seulement si l'écran appelant sait refaire le brouillon (`rePreparer`).
    const { onModeles, fenetre } = useModelesEnvoiStage({ departementId, typeMail: envoi?.type_mail, rePreparer,
                                                          configurationId: envoi?.configuration_id || null });
    if (envoi && envoi.mode === 'smtp' && envoi.portee === 'promotion') {
        return <><StageFenetrePromotion brouillon={envoi} departementId={departementId} onFermer={onFermer}
                                      onTermine={onTermine || (() => {})} onModeles={onModeles} />{fenetre}</>;
    }
    if (envoi && envoi.mode === 'smtp') {
        // Lot 3.5a-3 : les courriels des tuteurs ont leurs routes (un message, plusieurs étudiants) ;
        // la fenêtre renvoie le « contexte » du brouillon, le serveur relit tout à partir de lui.
        const api = envoi.type === 'tuteurs' ? {
            envoyer: (dep, corps) => stageAPI.acteEnvoiTuteurs('envoyer', dep, { ...corps, ...envoi.contexte }),
            essai:   (dep, corps) => stageAPI.acteEnvoiTuteurs('essai', dep, { ...corps, ...envoi.contexte }),
            mailto:  (dep, corps) => stageAPI.acteEnvoiTuteurs('mailto', dep, { ...corps, ...envoi.contexte })
        } : envoi.type === 'annuaire' ? {
            // Lot 3.5d-0 (E164) : un message libre à une entreprise de l'Annuaire.
            envoyer: (dep, corps) => stageAPI.acteEnvoiEntreprise('envoyer', dep, { ...corps, ...envoi.contexte }),
            essai:   (dep, corps) => stageAPI.acteEnvoiEntreprise('essai', dep, { ...corps, ...envoi.contexte }),
            mailto:  (dep, corps) => stageAPI.acteEnvoiEntreprise('mailto', dep, { ...corps, ...envoi.contexte })
        } : null;
        return <><StageFenetreEnvoi brouillon={envoi} departementId={departementId} onFermer={onFermer}
                                  onTermine={onTermine || (() => {})} api={api} serie={serie} onModeles={onModeles} />{fenetre}</>;
    }
    return <ModaleMailto envoi={envoi} onFermer={onFermer} serie={serie} />;
}

function ModaleMailto({ envoi, onFermer: fermerSeul, serie = null }) {
    const [copie, setCopie] = useState(null);   // null | true | false
    const [ouvert, setOuvert] = useState(false);
    const refAdresses = useRef(null);

    const groupe = envoi.mode === 'diffusion';
    const promotion = typeof envoi.nb_destinataires === 'number' && !envoi.adresse;

    // Qui reçoit ce message, d'après le serveur — et non d'après l'écran.
    const role = ROLES[envoi.destinataire_role] || ROLE_INCONNU;
    // ⚠️ « Relancer » ne se dit QUE sur un envoi qui en suit un autre. Sur un
    // premier envoi, c'est un contresens : on n'a encore relancé personne.
    const relance = envoi.relance === true;

    /**
     * ⚠️ La copie se fait À L'OUVERTURE, pas au second clic : le presse-papier
     * est prêt avant même que le client mail s'ouvre, ce qui économise un geste.
     * Elle n'a lieu que pour l'envoi groupé en mode liste — ailleurs, le
     * destinataire est déjà dans l'URL.
     */
    useEffect(() => {
        if (!promotion || groupe || !envoi.adresses) return;
        copier(envoi.adresses).then(setCopie);
    }, [promotion, groupe, envoi.adresses]);

    const recopier = useCallback(async () => {
        setCopie(await copier(envoi.adresses));
        if (refAdresses.current) refAdresses.current.select();
    }, [envoi.adresses]);

    const ouvrirMessage = useCallback(() => {
        setOuvert(true);
        window.location.href = envoi.mailto;
    }, [envoi.mailto]);
    // Lot 3.5b-3 — en série : « Terminé » passe au message suivant ; sans l'avoir ouvert, c'est « Passer ».
    const onFermer = serie ? () => serie.onSuivant(ouvert ? 'prepare' : 'passe') : fermerSeul;


    return (
        <StageModale
            titre={promotion
                ? 'Envoyer le lien à la promotion'
                : (relance ? 'Relancer ' + role.le : 'Écrire ' + role.a)}
            icone="fa-paper-plane"
            /**
             * ⚠️ UN BOUTON, UN CLIC, LE `mailto:` — ET RIEN D'AUTRE.
             *
             * J'ai essayé trois fois d'y mettre autre chose : le brouillon
             * téléchargeable en action principale, puis en action secondaire,
             * puis derrière une case à cocher mémorisée. Bertrand a refusé les
             * trois, le 2026-09-25, et il a eu raison à chaque fois — toutes
             * ajoutaient un geste ou une décision à un écran qui doit en
             * demander ZÉRO.
             *
             * ⚠️ Le vrai enseignement : quand une contrainte est INFRANCHISSABLE
             * (un corps de `mailto:` est du texte brut, mesuré), il ne faut pas
             * la contourner par des demi-mesures qui coûtent à l'utilisateur.
             * Il faut le dire, et aller chercher la seule porte qui l'enlève —
             * ici, l'envoi par le serveur (lot 3.S).
             */
            libelleValider={ouvert ? 'Rouvrir le message' : 'Ouvrir le message'}
            onValider={ouvrirMessage}
            libelleAnnuler={serie
                ? (ouvert ? (serie.position < serie.total ? 'Message suivant ▶' : 'Terminer la série') : 'Passer')
                : (ouvert ? 'Terminé' : 'Annuler')}
            onFermer={onFermer}
        >
            {serie && <BandeauSerie serie={serie} />}
            {/* ── Envoi groupé, mode liste copiable ─────────────────────── */}
            {promotion && !groupe && (
                <>
                    <div className="stg-encart stg-encart-info" style={{ marginBottom: 12 }}>
                        <i className="fa-solid fa-circle-info" />
                        <span>
                            <b>{envoi.nb_destinataires} étudiant{envoi.nb_destinataires > 1 ? 's' : ''}</b> —
                            leurs adresses {copie === true ? 'sont déjà copiées' : 'sont à copier'}.
                            Le message s'ouvrira avec l'objet et le texte remplis ;
                            il ne restera qu'à <b>coller les adresses en Cci</b> et envoyer.
                        </span>
                    </div>

                    {/* ⚠️ Les adresses restent VISIBLES : si le presse-papier est
                        perdu entre l'application et le client mail, l'assistante
                        peut recopier sans rouvrir la modale. */}
                    <div className="form-group" style={{ marginBottom: 8 }}>
                        <label htmlFor="adr-promo">
                            Adresses ({envoi.nb_destinataires}) — séparées par des points-virgules
                        </label>
                        <textarea
                            id="adr-promo" ref={refAdresses} className="input-sm"
                            readOnly value={envoi.adresses || ''}
                            style={{ height: 90, fontFamily: 'ui-monospace, monospace', fontSize: 11 }}
                            onFocus={(e) => e.target.select()}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <button type="button" className="btn btn-outline btn-sm" onClick={recopier}>
                            <i className="fa-solid fa-copy" /> Recopier les adresses
                        </button>
                        {copie === true && (
                            <span style={{ fontSize: 11, color: '#15803d', fontWeight: 600 }}>
                                <i className="fa-solid fa-check" /> Copiées
                            </span>
                        )}
                        {copie === false && (
                            <span style={{ fontSize: 11, color: '#b45309' }}>
                                <i className="fa-solid fa-triangle-exclamation" /> Copie automatique
                                impossible — sélectionnez le texte ci-dessus et copiez-le à la main.
                            </span>
                        )}
                    </div>
                </>
            )}

            {/* ── Envoi groupé via une liste ou un groupe du carnet ──────── */}
            {promotion && groupe && (
                <div className="stg-encart stg-encart-alerte" style={{ marginBottom: 12 }}>
                    <i className="fa-solid fa-triangle-exclamation" />
                    <span>
                        Le message partira à <b>{envoi.adresse_diffusion}</b>.
                        {' '}<b>L'application connaît {envoi.nb_connus} étudiant
                        {envoi.nb_connus > 1 ? 's' : ''}</b> dans cette promotion, mais elle
                        ne peut pas vérifier qui ce groupe contient —
                        {' '}<b>comparez ce nombre</b> avec ce que votre client mail affiche
                        avant d'envoyer.
                    </span>
                </div>
            )}

            {/* ── Relance individuelle ──────────────────────────────────── */}
            {!promotion && (
                <>
                    <div style={{ marginBottom: 10 }}>
                        Destinataire : <b>{envoi.adresse}</b>
                        {/* ⚠️ Le nom de l'ÉTUDIANT reste affiché — mais comme le
                            dossier concerné, jamais comme le destinataire. C'est
                            la confusion exacte que cette modale entretenait. */}
                        {envoi.etudiant && (
                            <span style={{ color: '#64748b' }}> · dossier de <b>{envoi.etudiant}</b></span>
                        )}
                    </div>
                    {/* Lot 3.4 bis : un RENVOI fabrique un nouveau lien et tue l'ancien —
                        l'écran doit le dire, pas l'inverse. */}
                    {envoi.lien_renouvele && (
                        <div className="stg-encart stg-encart-alerte" style={{ marginBottom: 12 }}>
                            <i className="fa-solid fa-rotate" />
                            <span>
                                Un <b>nouveau lien</b> remplace le précédent, qui <b>ne fonctionne plus</b> :
                                c'est celui de ce message qu'il faut envoyer.
                            </span>
                        </div>
                    )}
                    {envoi.jeton_nouveau === false && (
                        <div className="stg-encart stg-encart-info" style={{ marginBottom: 12 }}>
                            <i className="fa-solid fa-circle-info" />
                            <span>
                                Le lien déjà envoyé <b>reste valable</b> : l'application n'en
                                fabrique pas un second. Si {role.le} retrouve le premier
                                message, il fonctionnera toujours.
                            </span>
                        </div>
                    )}
                    {envoi.jeton_ouvert_le && (
                        <div className="stg-encart stg-encart-ok" style={{ marginBottom: 12 }}>
                            <i className="fa-solid fa-eye" />
                            <span>
                                Ce lien a <b>déjà été ouvert</b>. {role.Le} a donc bien reçu
                                le message : il ne l'a pas complété.
                            </span>
                        </div>
                    )}
                </>
            )}

            {/* ⚠️ Les étudiants sans adresse sont NOMMÉS. Les écarter en silence,
                c'est ne s'en apercevoir qu'au moment où leur fiche manque. */}
            {(envoi.sans_adresse || []).length > 0 && (
                <div className="stg-encart stg-encart-alerte" style={{ marginBottom: 12 }}>
                    <i className="fa-solid fa-user-slash" />
                    <span>
                        <b>{envoi.sans_adresse.length} étudiant{envoi.sans_adresse.length > 1 ? 's' : ''} sans
                        adresse</b> ne recevront rien : {envoi.sans_adresse.join(', ')}.
                        Complétez leur fiche dans le module Étudiants.
                    </span>
                </div>
            )}

            {/**
              * ⚠️ LE BROUILLON MIS EN FORME EST UNE OPTION, PAS LE CHEMIN.
              *
              * Il donne un message où le lien est posé sur son libellé — ce que
              * le `mailto:` ne saura jamais faire, son corps étant du texte brut
              * (mesuré le 2026-09-25 : le HTML en ressort avec ses chevrons).
              *
              * Mais il passe par un téléchargement, donc deux gestes de plus par
              * message. Il reste donc **en second, discret** : utile pour un
              * message qui compte — une entreprise qu'on sollicite pour la
              * première fois — inutile pour les envois de tous les jours.
              *
              * ⚠️ Le vrai remède est le lot 3.S : l'envoi serveur produit ce
              * même corps HTML, en un clic et sans fichier. Ce code-ci n'est pas
              * du travail perdu, c'est sa moitié déjà écrite.
              */}
            <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#64748b' }}>
                    Voir le message qui sera préparé
                </summary>
                <div style={{ marginTop: 8, padding: '10px 12px', background: '#f8fafc',
                              border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>{envoi.sujet}</div>
                    <div style={{ whiteSpace: 'pre-wrap', fontSize: 11, lineHeight: 1.5 }}>{envoi.corps}</div>
                </div>
            </details>

            {/* ⚠️ « préparé », jamais « envoyé » — voir l'en-tête (écart E8). */}
            <div className="stg-encart stg-encart-alerte" style={{ marginTop: 12, marginBottom: 0 }}>
                <i className="fa-solid fa-circle-info" />
                <span>
                    L'application <b>prépare</b> le message : elle ouvre votre client mail,
                    mais ne peut pas savoir s'il part. Le suivi affichera
                    {' '}<b>« lien préparé »</b>, et « lien ouvert » seulement quand le
                    destinataire aura cliqué.
                </span>
            </div>
        </StageModale>
    );
}

export default StageModaleEnvoi;
